/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import apiClient from '../api/client';
import posthog from 'posthog-js';
import { DEFAULT_LOGIN_ROUTE, accountLoginPath, setLoginAccountSlug } from '../constants/loginRoutes';
import { resetLiveSessionReconnectAttempts } from '../utils/liveSessionReconnect';

const AuthContext = createContext(null);

// ── Branding constants ──────────────────────────────────────────────────────
const DAISY_THEMES = [
  "light", "dark", "cupcake", "bumblebee", "emerald", "corporate", "synthwave",
  "retro", "cyberpunk", "valentine", "halloween", "garden", "forest", "aqua",
  "lofi", "pastel", "fantasy", "wireframe", "black", "luxury", "dracula",
  "cmyk", "autumn", "business", "acid", "lemonade", "night", "coffee", "winter",
  "dim", "nord", "sunset", "caramellatte",
];
const VALID_THEMES = new Set(DAISY_THEMES);

function validateTheme(theme) {
  if (theme && VALID_THEMES.has(theme)) return theme;
  if (theme) console.warn(`[AuthContext] Invalid theme "${theme}", falling back to "light"`);
  return 'light';
}

function validateLogoUrl(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  console.warn('[AuthContext] Invalid logo URL protocol, falling back to default');
  return null;
}

// brand* fields from /auth/me are already resolved sub-account → account;
// null means the platform default (ServiceHub logo / favicon.ico). The subAccount*
// fallback covers a user cached in localStorage before brand* existed.
function extractBranding(userData) {
  return {
    logoUrl: validateLogoUrl(userData?.brandLogoUrl ?? userData?.subAccountLogoUrl ?? null),
    faviconUrl: validateLogoUrl(userData?.brandFaviconUrl ?? userData?.subAccountFaviconUrl ?? null),
  };
}

// ── Theme preference ────────────────────────────────────────────────────────
// A theme the user picked on the Appearance page is kept per user in this browser
// and always wins over the workspace (sub-account / account) branding theme.
const LEGACY_THEME_KEY = 'app-theme';
const userThemeKey = (userId) => `user-theme:${userId}`;

function getUserTheme(userId) {
  if (!userId) return null;
  try {
    const theme = localStorage.getItem(userThemeKey(userId));
    return theme && VALID_THEMES.has(theme) ? theme : null;
  } catch {
    return null;
  }
}

function setUserTheme(userId, theme) {
  if (!userId) return;
  try {
    if (theme) localStorage.setItem(userThemeKey(userId), theme);
    else localStorage.removeItem(userThemeKey(userId));
  } catch (e) {
    console.warn('[AuthContext] Could not persist theme to localStorage:', e);
  }
}

/** Moves a theme saved by older versions (global `app-theme`) to the user's own key. */
function migrateLegacyTheme(userId) {
  try {
    const legacy = localStorage.getItem(LEGACY_THEME_KEY);
    if (legacy === null) return;
    if (!getUserTheme(userId) && VALID_THEMES.has(legacy) && legacy !== 'light') {
      setUserTheme(userId, legacy);
    }
    localStorage.removeItem(LEGACY_THEME_KEY);
  } catch {
    // storage unavailable — nothing to migrate
  }
}

/** User's own theme → workspace branding theme → light */
export function resolveTheme(userData) {
  return validateTheme(
    getUserTheme(userData?.id) ?? userData?.brandTheme ?? userData?.subAccountTheme ?? null,
  );
}

// ── Storage helpers ─────────────────────────────────────────────────────────
const clearStorage = () => {
  localStorage.removeItem('auth_access_token');
  localStorage.removeItem('auth_refresh_token');
  localStorage.removeItem('auth_user');
  localStorage.removeItem('auth_account_name');
  resetLiveSessionReconnectAttempts();
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accountName, setAccountName] = useState(null);
  const [userScope, setUserScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const [currentFaviconUrl, setCurrentFaviconUrl] = useState(null);
  const [currentTheme, setCurrentTheme] = useState('light');
  // Whether the user picked their own theme (it overrides the workspace branding theme)
  const [hasThemeChoice, setHasThemeChoice] = useState(false);

  // Apply favicon to document
  useEffect(() => {
    const url = currentFaviconUrl || '/favicon.ico';
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
  }, [currentFaviconUrl]);

  // Apply theme to document. Only an explicit choice (chooseTheme) is persisted, so a
  // workspace branding theme is never mistaken for the user's own preference.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);

  const applyBranding = useCallback((userData) => {
    const { logoUrl, faviconUrl } = extractBranding(userData);
    setCurrentLogoUrl(logoUrl);
    setCurrentFaviconUrl(faviconUrl);
    setCurrentTheme(resolveTheme(userData));
    setHasThemeChoice(!!getUserTheme(userData?.id));
  }, []);

  // Restore auth state and branding from localStorage on mount
  useEffect(() => {
    const restore = async () => {
      try {
        const savedToken = localStorage.getItem('auth_access_token');
        const savedUser = localStorage.getItem('auth_user');
        if (savedToken && savedUser) {
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          setUserScope(parsedUser?.scope || 'platform');
          setAccountName(localStorage.getItem('auth_account_name'));
          migrateLegacyTheme(parsedUser?.id);
          applyBranding(parsedUser);
        }
      } catch {
        clearStorage();
      } finally {
        setLoading(false);
      }
    };
    restore();
  }, [applyBranding]);

  // Identify the user in PostHog whenever user state changes.
  // Uses the posthog-js singleton directly (not the React hook) so the effect
  // dependency is purely on `user` — the singleton is always the same reference
  // and has a built-in call queue, so identify() is safe even before full init.
  useEffect(() => {
    if (!user?.id) return;
    posthog.identify(user.id, {
      email: user.email,
      displayName: user.displayName,
      accountUID: user.accountUID,
      subAccountUID: user.subAccountUID,
      scope: user.scope,
      ...(user.role ? { roles: [user.role] } : {}),
    });
  }, [user]);

  /**
   * @param loginRoute resolved via resolveLoginRoute(): the default route posts
   *   `{ email, password, subAccountUID?, slug? }` to /auth/login; an external
   *   route posts `{ username, password }` to its authPath, which returns the
   *   tokens under `serviceHub`.
   * @param accountSlug set on /login/:accountSlug — posts `{ username, password }` to
   *   /auth/accounts/login/:accountSlug instead (loginRoute is ignored): the server signs in
   *   through Pixo or locally, as the account is configured, and returns the tokens under
   *   `serviceHub`. Restricts login to that account and its sub-accounts, and makes logout
   *   return to that page.
   */
  const login = useCallback(async (email, password, subAccountUID, slug, loginRoute = DEFAULT_LOGIN_ROUTE, accountSlug) => {
    let tokens;
    if (accountSlug) {
      const { data } = await apiClient.post(accountLoginPath(accountSlug), {
        username: email,
        password,
      });
      tokens = data?.serviceHub;
      if (!tokens?.accessToken) {
        throw new Error('Unexpected response from authentication service');
      }
    } else if (loginRoute.isExternal) {
      const { data } = await apiClient.post(loginRoute.authPath, {
        username: email,
        password,
      });
      tokens = data?.serviceHub;
      if (!tokens?.accessToken) {
        throw new Error('Unexpected response from authentication service');
      }
    } else {
      const { data } = await apiClient.post(loginRoute.authPath, {
        email,
        password,
        ...(subAccountUID && { subAccountUID }),
        ...(slug && { slug }),
      });
      tokens = data;
    }
    setLoginAccountSlug(accountSlug || null);
    const { accessToken, refreshToken } = tokens;

    localStorage.setItem('auth_access_token', accessToken);
    localStorage.setItem('auth_refresh_token', refreshToken);
    // A fresh login restores the full allowance of manual live session reconnects.
    resetLiveSessionReconnectAttempts();
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

    const { data: userData } = await apiClient.get('/auth/me');
    const scope = userData?.scope || 'platform';
    const userWithScope = { ...userData, scope };

    localStorage.setItem('auth_user', JSON.stringify(userWithScope));
    setUser(userWithScope);
    setUserScope(scope);
    setAccountName(null);
    migrateLegacyTheme(userData?.id);
    applyBranding(userData);

    return userWithScope;
  }, [applyBranding]);

  const switchContext = useCallback(async (accountUID, subAccountUID) => {
    const { data } = await apiClient.post('/auth/switch-context', {
      accountUID,
      ...(subAccountUID && { subAccountUID }),
    });

    localStorage.setItem('auth_access_token', data.accessToken);
    localStorage.setItem('auth_account_name', data.accountName);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;

    const { data: userData } = await apiClient.get('/auth/me');
    const scope = data.scope || userData?.scope || 'platform';
    const userWithScope = { ...userData, scope };

    localStorage.setItem('auth_user', JSON.stringify(userWithScope));
    setUser(userWithScope);
    setUserScope(scope);
    setAccountName(data.accountName);
    applyBranding(userData);

    return data;
  }, [applyBranding]);

  const resetContext = useCallback(async () => {
    const refreshToken = localStorage.getItem('auth_refresh_token');
    if (!refreshToken) { return; }

    const { data } = await apiClient.post('/auth/refresh', { refreshToken }, {
      headers: { Authorization: undefined },
    });

    localStorage.setItem('auth_access_token', data.accessToken);
    if (data.refreshToken) { localStorage.setItem('auth_refresh_token', data.refreshToken); }
    localStorage.removeItem('auth_account_name');
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;

    const { data: userData } = await apiClient.get('/auth/me');
    const scope = userData?.scope || 'platform';
    const userWithScope = { ...userData, scope };

    localStorage.setItem('auth_user', JSON.stringify(userWithScope));
    setUser(userWithScope);
    setUserScope(scope);
    setAccountName(null);
    // Back to the user's own context: its branding, or the user's own theme
    applyBranding(userData);
  }, [applyBranding]);

  const updateProfile = useCallback((userData) => {
    const updatedUser = { ...user, ...userData };
    localStorage.setItem('auth_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  }, [user]);

  /** Re-reads the active context's branding, e.g. after editing account / sub-account branding. */
  const refreshBranding = useCallback(async () => {
    const { data: userData } = await apiClient.get('/auth/me');
    const updatedUser = { ...user, ...userData };
    localStorage.setItem('auth_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
    applyBranding(updatedUser);
  }, [user, applyBranding]);

  /** Saves the user's own theme choice; it overrides the workspace branding theme. */
  const chooseTheme = useCallback((themeId) => {
    const theme = validateTheme(themeId);
    setUserTheme(user?.id, theme);
    setCurrentTheme(theme);
    setHasThemeChoice(true);
  }, [user?.id]);

  /** Drops the user's own theme choice and goes back to the workspace branding theme. */
  const clearThemeChoice = useCallback(() => {
    setUserTheme(user?.id, null);
    setCurrentTheme(resolveTheme(user));
    setHasThemeChoice(false);
  }, [user]);

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('auth_refresh_token');
      if (refreshToken) {
        await apiClient.post('/auth/logout', {}, {
          headers: {
            Authorization: `Bearer ${refreshToken}`
          }
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      clearStorage();
      delete apiClient.defaults.headers.common['Authorization'];
      setUser(null);
      setUserScope(null);
      setAccountName(null);
      setCurrentLogoUrl(null);
      setCurrentFaviconUrl(null);
      setCurrentTheme('light');
      setHasThemeChoice(false);
      // Reset PostHog session — next event will get a fresh anonymous distinctId
      posthog.reset();
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      userScope,
      accountName,
      currentLogoUrl,
      currentFaviconUrl,
      currentTheme,
      chooseTheme,
      clearThemeChoice,
      hasThemeChoice,
      login,
      switchContext,
      resetContext,
      logout,
      updateProfile,
      refreshBranding,
      loading,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
